import { useEffect, useState, useContext, useRef } from "react";
import scheduleBasedRequest from "../../request/scheduleBasedRequest";
import { CombinedContext } from "../../context/combinedContext";
import "./bookingModal.css";
/* Not used by this file any more — it's the only importer of the shared
   .sf-book-* shell that six other components still depend on. See the
   header comment in that file. */
import "./sharedModalStyles.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import translations from "../../translations/translations";
import BookingModalSkeleton from "./bookingModalSkeleton/bookingModalSkeleton";
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Clock, Info, MapPin, Phone, User, X } from "lucide-react";
import CustomSelect from "../common/customSelect/customSelect";
import BannerMessage from "../common/bannerMessage/bannerMessage";
import RSFNewAutocomplete from "../../utils/addressAutofill/addressAutocomplete";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import PhoneInput from "../common/phoneInput/phoneInput";
import StaffDropdown from "../common/StaffDropdown/StaffDropdown";
import useModalDismiss from "../common/useModalDismiss";

dayjs.extend(utc);
dayjs.extend(timezone);

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

/* Same shape as the shop and discover pages. The modal used to hardcode "$",
   so a DOP shop's RD$100 service read as $100 at the point of purchase. */
const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const BookingModal = ({ open, onClose, availabilityId, shopData, initialstaffId, initialServiceId, initialServices, mode, existingBooking }) => {
	const { selectedstaff, setSelectedstaff, userData, language } = useContext(CombinedContext);
	const [step, setStep] = useState(0);
	const [date, setDate] = useState(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
	const [selTime, setSelTime] = useState("");
	const [timeData, setTimeData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [dayIndex, setDayIndex] = useState(0);
	const [startDate, setStartDate] = useState(dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD"));
	const [toast, setToast] = useState(null);
	const [customerName, setCustomerName] = useState("");
	const [customerEmail, setCustomerEmail] = useState("");
	const [selectedServices, setSelectedServices] = useState([]);
	const [timePeriod, setTimePeriod] = useState("Morning");
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(true);
	const [validationError, setValidationError] = useState("");
	const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
	const [homeAddress, setHomeAddress] = useState({
		address: "",
		city: "",
		country: "",
		lat: null,
		lng: null,
	});
	const [homePhone, setHomePhone] = useState("");
    const [addressDescription, setAddressDescription] = useState("");


	const slotsRef = useRef(null);
	const daysRef = useRef(null);
	const bodyRef = useRef(null);
	const daysSettled = useRef(false);

	const navigate = useNavigate();

	/* Read before the early return now — the step list and its checks are
	   built during render, not only inside the JSX. */
	const t = translations[language].booking;

	const isLoggedIn = !!userData?.user?.userId;

	/* Reschedule reuses this modal but posts to rescheduleBooking, which carries
	   none of the contact fields — so the sections that collect them stay hidden
	   and the checks below have to skip too, or the confirm button dead-ends. */
	const isReschedule = mode === "reschedule" || !!existingBooking;

	/* /login and /me return the DynamoDB record (phone); the ID-token decode in
	   getInitialUserData only ever sets phoneNumber. Read both, or a session
	   hydrated from the token alone looks like an account with no phone. */
	const accountPhone = (userData?.user?.phone || userData?.user?.phoneNumber || "").trim();

	/* createBooking rejects an empty servicePhone with a bare "Missing required
	   fields". Anything typed in the modal wins over what's on the account. */
	const contactPhone = (homePhone || accountPhone).trim();

	const mounted = useRef(false);

	useEffect(() => {
		mounted.current = true;
		return () => (mounted.current = false);
	}, []);

	/* Escape closes. The backdrop deliberately does not: a stray click there
	   would throw away a part-filled booking. */
	useModalDismiss(onClose, { enabled: open });

	useEffect(() => {
		if (!open) return;

		let seeded = [];
		if (initialServices?.length) {
			seeded = initialServices.map((s) => shopData.services.find((x) => x.serviceId === s.serviceId)).filter(Boolean);
		} else if (initialServiceId) {
			const s = shopData.services.find((x) => x.serviceId === initialServiceId);
			if (s) seeded = [s];
		}
		setSelectedServices(seeded);

		const candidate = initialstaffId || "";
		const valid =
			candidate &&
			seeded.every((srv) => shopData.staffs.find((st) => st.staffId === candidate || st.userId === candidate)?.service?.includes(srv.serviceId));
		setSelectedstaff(valid ? candidate : "");

		const today = dayjs().tz("America/Santo_Domingo").format("YYYY-MM-DD");
		setSelTime("");
		setDate(today);
		setStartDate(today);
		setDayIndex(0);
		setStaffDropdownOpen(false);
		setStep(0);
	}, [open]);

	useEffect(() => {
		if (existingBooking && !isLoggedIn) {
			setCustomerName(existingBooking.customerName || "");
			setCustomerEmail(existingBooking.customerEmail || "");
			setHomeAddress({
				address: existingBooking.address || "",
				city: existingBooking.city || "",
				country: existingBooking.country || "",
				lat: existingBooking.lat || null,
				lng: existingBooking.lng || null,
			});
			setHomePhone(existingBooking.servicePhone || existingBooking.phone || "");
            setAddressDescription(existingBooking.addressDescription || "");
		}
	}, [existingBooking, isLoggedIn]);

	useEffect(() => {
		if (!open) return;

		const scrollY = window.scrollY;
		const body = document.body;

		body.style.position = "fixed";
		body.style.top = `-${scrollY}px`;
		body.style.left = "0";
		body.style.right = "0";
		body.style.width = "100%";

		return () => {
			body.style.position = "";
			body.style.top = "";
			body.style.left = "";
			body.style.right = "";
			body.style.width = "";
			window.scrollTo(0, scrollY);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		async function loadAvailability() {
			try {
				setLoading(true);
				const data = await scheduleBasedRequest.getAvailability(availabilityId, {
					startDate: startDate,
				});

				let targetDate = startDate; // ✅ use startDate, not stale `date`
				let targetIndex = 0; // ✅ always reset to 0, not stale `dayIndex`

				if (data?.week) {
					const weekEntries = Object.entries(data.week);
					const currentValid = data.week[startDate]?.totalSlots > 0;

					if (!currentValid) {
						const nextOpen = weekEntries.find(([_, info]) => info.totalSlots > 0);
						if (nextOpen) {
							targetDate = nextOpen[0];
							targetIndex = weekEntries.findIndex(([d]) => d === nextOpen[0]);
						}
					}
				}

				setTimeData(data);
				setDate(targetDate);
				setDayIndex(targetIndex);
			} catch (err) {
				console.error(err);
			} finally {
				setLoading(false);
			}
		}
		loadAvailability();
	}, [open, availabilityId, startDate]); // ✅ no `date` or `dayIndex` needed

	function filterSlotsByDuration(slots, totalDuration) {
		if (!totalDuration) return slots;

		const neededSlots = Math.ceil(totalDuration / 15);

		return slots.filter((slot, index) => {
			for (let i = 0; i < neededSlots; i++) {
				const next = slots[index + i];
				if (!next) return false;

				if (i > 0) {
					const [hPrev, mPrev] = slots[index + i - 1].start.split(":").map(Number);
					const [hNext, mNext] = next.start.split(":").map(Number);

					const prevMinutes = hPrev * 60 + mPrev;
					const nextMinutes = hNext * 60 + mNext;

					if (nextMinutes - prevMinutes !== 15) {
						return false;
					}
				}
			}
			return true;
		});
	}

	const hasHomeService = selectedServices.some((s) => s.isHomeService === true);
	const hasInShopService = selectedServices.some((s) => s.isHomeService !== true);

	const currentDayStr = date;
	const dayData = timeData?.week?.[currentDayStr];
	const dayAvailability = dayData?.staffs ?? [];
	const selectedStaffData = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : null;

	const staffServiceIds = selectedStaffData?.service || [];
	const filteredServices = selectedstaff ? shopData.services.filter((svc) => staffServiceIds.includes(svc.serviceId)) : shopData.services;
	const allSlotsUnique = Array.from(new Map(dayAvailability.flatMap((b) => (b.slots || []).map((s) => [s.start, s]))).values());

	const totalPrice = selectedServices.reduce((sum, s) => sum + (s.price || 0), 0);
	const totalTime = selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0);

	/* Services in one shop share a currency; fall back to the shop's own list
	   so the total still reads correctly before anything is selected. */
	const currency = selectedServices[0]?.currency || shopData?.services?.[0]?.currency;

	const maxTransitMinutes = hasHomeService ? Math.max(...selectedServices.map((s) => s.transitMinutes || 0)) : 0;

	const totalDurationWithTransit = hasHomeService ? totalTime + maxTransitMinutes : totalTime;

	function sortSlots(slots = []) {
		return [...slots].sort((a, b) => {
			const [hA, mA] = a.start.split(":").map(Number);
			const [hB, mB] = b.start.split(":").map(Number);
			return hA * 60 + mA - (hB * 60 + mB);
		});
	}

	function canStaffTakeHomeSlot(staff, slotStartStr) {
		if (!hasHomeService) return true;

		const tz = "America/Santo_Domingo";
		const start = dayjs.tz(`${date} ${slotStartStr}`, tz);

		if (!staff?.shiftStart) return true;

		const shiftStart = dayjs.tz(`${date} ${staff.shiftStart}`, tz);
		const minutesSinceShiftStart = start.diff(shiftStart, "minute");

		// first slot(s) near shift start are allowed even without prior slot
		if (minutesSinceShiftStart < maxTransitMinutes) {
			return true;
		}

		const neededStartStr = start.subtract(maxTransitMinutes, "minute").format("HH:mm");
		const staffSlotStarts = (staff.slots || []).map((s) => s.start);

		return staffSlotStarts.includes(neededStartStr);
	}

	function getValidSlotsForStaff(staff) {
		if (!staffCanHandleAllServices(staff)) return [];

		const staffSlots = sortSlots(staff.slots || []);
		const durationValid = filterSlotsByDuration(staffSlots, totalDurationWithTransit);

		return durationValid.filter((slot) => canStaffTakeHomeSlot(staff, slot.start));
	}

	const rawSlots = selectedstaff
		? dayAvailability.find((b) => b.staffId === selectedstaff)?.slots || []
		: Array.from(
				new Map(
					dayAvailability
						.filter((b) => staffCanHandleAllServices(b))
						.flatMap((staff) => {
							const staffSlots = [...(staff.slots || [])].sort((a, b) => {
								const [hA, mA] = a.start.split(":").map(Number);
								const [hB, mB] = b.start.split(":").map(Number);
								return hA * 60 + mA - (hB * 60 + mB);
							});

							return filterSlotsByDuration(staffSlots, totalDurationWithTransit);
						})
						.map((slot) => [slot.start, slot]),
				).values(),
			);

	const sortedRawSlots = rawSlots.sort((a, b) => {
		const [hA, mA] = a.start.split(":").map(Number);
		const [hB, mB] = b.start.split(":").map(Number);
		return hA * 60 + mA - (hB * 60 + mB);
	});

	const slotsAfterDuration = filterSlotsByDuration(sortedRawSlots, totalDurationWithTransit);

	function slotRespectsTransit(slotStartStr) {
		if (!hasHomeService) return true;

		const tz = "America/Santo_Domingo";
		const start = dayjs.tz(`${date} ${slotStartStr}`, tz);

		// Determine staff shift start
		const staff = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : dayAvailability;

		if (!staff || !staff.shiftStart) return true;

		const shiftStart = dayjs.tz(`${date} ${staff.shiftStart}`, tz);

		// minutes since shift start
		const minutesSinceShiftStart = start.diff(shiftStart, "minute");

		// Allow slots inside transit window after shift start
		if (minutesSinceShiftStart < maxTransitMinutes) {
			return true;
		}

		// Otherwise enforce transit buffer
		const effectiveStart = start.subtract(maxTransitMinutes, "minute");
		const neededStartStr = effectiveStart.format("HH:mm");

		const staffSlots = rawSlots.map((s) => s.start);

		return staffSlots.includes(neededStartStr);
	}

	// const slotsToShow = slotsAfterDuration.filter((slot) => slotRespectsTransit(slot.start));
	const slotsToShow = selectedstaff
		? getValidSlotsForStaff(dayAvailability.find((b) => b.staffId === selectedstaff) || {})
		: Array.from(new Map(dayAvailability.flatMap((staff) => getValidSlotsForStaff(staff).map((slot) => [slot.start, slot]))).values()).sort((a, b) => {
				const [hA, mA] = a.start.split(":").map(Number);
				const [hB, mB] = b.start.split(":").map(Number);
				return hA * 60 + mA - (hB * 60 + mB);
			});

	// If at least one selected, lock the type
	const lockedType = hasHomeService ? "home" : hasInShopService ? "shop" : null;

	function canAddService(service) {
		if (!lockedType) return true;
		if (lockedType === "home") return service.isHomeService === true;
		return service.isHomeService !== true;
	}

	useEffect(() => {
		if (!selTime) return;

		const stillValid = slotsToShow.some((s) => s.start === selTime);

		if (!stillValid) {
			setSelTime("");
			setToast(translations[language].booking.toast.timeInvalidated);
		}
	}, [slotsToShow, selTime]);

	const prevHasHome = useRef(hasHomeService);
	
	useEffect(() => {
		if (prevHasHome.current && !hasHomeService) {
			setHomeAddress({ address: "", city: "", country: "", lat: null, lng: null });
			setHomePhone("");
			setAddressDescription("");
		}
		prevHasHome.current = hasHomeService;
	}, [hasHomeService]);

	function staffCanHandleAllServices(staff) {
		if (!selectedServices.length) return true;

		const staffServices = staff.service || []; // array of serviceIds

		// ensure all selected serviceIds are included in staffServices
		return selectedServices.every((srv) => staffServices.includes(srv.serviceId));
	}

	function changeWeek(offset) {
		const newStart = dayjs(startDate)
			.add(offset * 7, "day")
			.format("YYYY-MM-DD");
		setStartDate(newStart);
		setDayIndex(0);
		setSelTime("");
	}

	function fmtDayLabel(iso) {
		return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
			weekday: "short",
			day: "numeric",
		});
	}

	function getTimePeriod(hour) {
		if (hour < 12) return "Morning";
		if (hour < 17) return "Afternoon";
		return "Evening";
	}

	const availablePeriods = new Set(slotsToShow.map((slot) => getTimePeriod(parseInt(slot.start.split(":")[0]))));

	/* ============================================================
	   STEPS
	   Two: everything you choose, then everything you're about to
	   agree to.

	   Within the first step the order still matters — services lead,
	   because the duration they add is what decides which slots are
	   offerable at all — and the contact sections only appear when
	   there's something to collect, so a logged-in customer with a
	   phone on file booking in-shop never sees them.
	   ============================================================ */
	const needsGuestInfo = !isLoggedIn && !isReschedule;
	const needsContactPhone = isLoggedIn && !accountPhone && !isReschedule;
	const needsHomeDetails = hasHomeService && !isReschedule;

	const steps = [
		{ id: "book", label: t.steps.book },
		{ id: "review", label: t.steps.review },
	];

	const stepIndex = Math.min(step, steps.length - 1);
	const stepId = steps[stepIndex].id;
	const isLastStep = stepIndex === steps.length - 1;

	/* Checked in the order the sections appear, so Next points at the first
	   thing missing rather than the last. */
	function stepIssue(id) {
		if (id === "book") {
			if (selectedServices.length === 0) return t.toast.missingService;
			if (!selTime) return t.toast.missingTime;
			if (needsGuestInfo && (!customerName.trim() || !customerEmail.trim() || !homePhone.trim())) return t.toast.missingGuest;
			if (!isReschedule && !contactPhone) return t.toast.missingPhone;
			if (needsHomeDetails && (!homeAddress.address || !homeAddress.lat || !homeAddress.lng)) return t.toast.missingAddress;
		}

		return "";
	}

	function firstIssue() {
		for (let i = 0; i < steps.length; i++) {
			const message = stepIssue(steps[i].id);
			if (message) return { index: i, message };
		}
		return null;
	}

	function goToStep(i) {
		setStep(Math.max(0, Math.min(i, steps.length - 1)));
	}

	function goNext() {
		const message = stepIssue(stepId);
		if (message) return setValidationError(message);
		goToStep(stepIndex + 1);
	}

	/* The day strip scrolls once seven pills no longer fit, so keep the chosen
	   day in view. Placed instantly on first paint, animated after that.
	   Re-runs on stepId too: the strip only mounts with the first step, so
	   coming back to it would otherwise land on an off-screen day. */
	useEffect(() => {
		if (loading) return;
		const active = daysRef.current?.querySelector(".is-active");
		if (!active) return;
		active.scrollIntoView({ block: "nearest", inline: "center", behavior: daysSettled.current ? "smooth" : "auto" });
		daysSettled.current = true;
	}, [dayIndex, startDate, loading, stepId]);

	/* Each step opens at the top of its own scroll, with no staff list left
	   open and no complaint left over from the step before. */
	useEffect(() => {
		if (bodyRef.current) bodyRef.current.scrollTop = 0;
		setStaffDropdownOpen(false);
		setValidationError("");
	}, [stepIndex]);

	async function confirmBooking() {
		setValidationError("");
		setConfirmLoading(true);

		/* Confirm only sits on the last step, but stepping back and swapping a
		   service can invalidate an earlier one — so re-run every step's check
		   and land the customer on the step that needs fixing. */
		const issue = firstIssue();
		if (issue) {
			setConfirmLoading(false);
			goToStep(issue.index);
			setValidationError(issue.message);
			return;
		}

		/* Logged-in bookings used to skip this entirely: the phone field only
		   rendered for guests, so an account with no phone on file sent
		   servicePhone: "" and the server answered "Missing required fields"
		   with nothing pointing at the cause. */
		if (!isReschedule && !contactPhone) {
			setConfirmLoading(false);
			setValidationError(t.toast.missingPhone);
			return;
		}

		const selectedstaffData = selectedstaff ? dayAvailability.find((b) => b.staffId === selectedstaff) : null;

		const availableStaffs = dayAvailability.filter((staff) => {
			if (!staffCanHandleAllServices(staff)) return false;

			const staffSlots = [...(staff.slots || [])].sort((a, b) => {
				const [hA, mA] = a.start.split(":").map(Number);
				const [hB, mB] = b.start.split(":").map(Number);
				return hA * 60 + mA - (hB * 60 + mB);
			});

			const validSlots = filterSlotsByDuration(staffSlots, totalDurationWithTransit).filter((slot) => slotRespectsTransit(slot.start));

			return validSlots.some((slot) => slot.start === selTime);
		});

		const fallbackstaffData = availableStaffs.length > 0 ? availableStaffs[Math.floor(Math.random() * availableStaffs.length)] : null;

		const chosenstaff = selectedstaffData?.staffId || fallbackstaffData?.staffId;

		const chosenstaffScheduleId = selectedstaffData?.scheduleId || fallbackstaffData?.scheduleId;

		const staffEmail = selectedstaffData?.email || fallbackstaffData?.email;

		const staffName = selectedstaffData
			? `${selectedstaffData.firstName || ""} ${selectedstaffData.lastName || ""}`.trim()
			: fallbackstaffData
				? `${fallbackstaffData.firstName || ""} ${fallbackstaffData.lastName || ""}`.trim()
				: "";

		if (!chosenstaff) {
			setConfirmLoading(false);
			setToast("No staff available for that time");
			return;
		}

		// ✅ Use shop timezone for everything
		const tz = "America/Santo_Domingo";
		const [hours, minutes] = selTime.split(":").map(Number);

		// Create the start/end in Santo Domingo time
		const startLocal = dayjs.tz(`${date} ${selTime}`, tz);

		const baseDuration = selectedServices.reduce((sum, s) => sum + (s.duration || 0), 0);

		const transitMinutes = hasHomeService ? Math.max(...selectedServices.map((s) => s.transitMinutes || 0)) : 0;

		const totalDuration = baseDuration + transitMinutes;

		const endLocal = startLocal.add(totalDuration, "minute");

		// ✅ Prevent booking in the past
		const now = dayjs().tz(tz);
		if (startLocal.isBefore(now)) {
			setConfirmLoading(false);
			setToast(translations[language].booking.toast.pastTime);
			return;
		}

		const payloadServices = selectedServices.map((s) => ({
			serviceId: s.serviceId,
			serviceNameEn: s.nameEn || s.name,
			serviceNameEs: s.nameEs || s.name,
			duration: s.duration || 0,
			isHomeService: s.isHomeService === true,
		}));

		const bookingPayload = {
			shopId: shopData.shopId,
			shopName: shopData.name,
			staffId: chosenstaff,
			scheduleId: chosenstaffScheduleId,
			staffsEmail: staffEmail,
			staffName: staffName,
			services: payloadServices,
			startTime: startLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
			endTime: endLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
			address: shopData.address || "",
			city: shopData.city || "",
			lat: shopData.lat || "",
			lng: shopData.lng || "",
			country: shopData.country || "",
			postalCode: shopData.postalCode || "",
			phone: shopData.phone || "",
			servicePhone: contactPhone,
		};

		if (hasHomeService) {
			bookingPayload.isHomeService = true;
			bookingPayload.transitMinutes = maxTransitMinutes;

			bookingPayload.serviceAddress = homeAddress.address;
			bookingPayload.serviceCity = homeAddress.city;
			bookingPayload.serviceCountry = homeAddress.country;
			bookingPayload.servicePostalCode = homeAddress.postalCode || "";
			bookingPayload.serviceLat = homeAddress.lat;
			bookingPayload.serviceLng = homeAddress.lng;
		} else {
			bookingPayload.isHomeService = false;
			bookingPayload.transitMinutes = 0;
		}

		if (isLoggedIn) {
			bookingPayload.customerId = userData.user.userId;
			bookingPayload.customerEmail = userData.user.email;
			bookingPayload.customerName = `${userData.user.firstName} ${userData.user.lastName}`;
		} else {
			bookingPayload.customerName = customerName;
			bookingPayload.customerEmail = customerEmail;
		}

		if (mode === "reschedule" && existingBooking?.bookingId) {
			try {
				const token = isLoggedIn ? userData?.cognitoTokens?.IdToken : null;

				await scheduleBasedRequest.rescheduleBooking(
					existingBooking.bookingId,
					{
						newStartTime: startLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
						newEndTime: endLocal.tz("America/Santo_Domingo").format("YYYY-MM-DDTHH:mm:ss"),
						newStaffId: selectedstaff || existingBooking.staffId,
						newStaffName: staffName || existingBooking.staffName,
						newServices: selectedServices.map((s) => ({
							serviceId: s.serviceId,
							serviceNameEn: s.nameEn || s.name,
							duration: s.duration,
						})),
					},
					token,
				);

				setToast(translations[language].booking.toast.rescheduled);
				navigate(`/booking/${existingBooking.bookingId}`);
				setTimeout(() => onClose?.(), 1000);
			} catch (err) {
				console.error("💥 Error rescheduling:", err);
				setValidationError(translations[language].booking.toast.couldNotReschedule);
				setConfirmLoading(false);
			}
			return;
		}

		try {
			const result = await scheduleBasedRequest.createBooking(bookingPayload);
			setToast(translations[language].booking.toast.bookingConfirmed);
			navigate(`/booking/${result.booking.bookingId}`);
		} catch (err) {
			console.error(err);
			setValidationError(translations[language].booking.toast.couldNotConfirm);
			setConfirmLoading(false);
		}
	}

	function parseLocalDate(str) {
		const [y, m, d] = str.split("-").map(Number);
		return dayjs.tz(`${str} 12:00`, "America/Santo_Domingo").toDate();
	}

	function totalServicesText(n) {
		const t = translations[language].booking.summary;

		if (n === 0) return t.noServices;
		if (n === 1) return t.oneService;
		return t.multipleServices.replace("{n}", n);
	}

	function formatTime24To12(time24, language = "en") {
		if (!time24) return "";

		const [h, m] = time24.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = m.toString().padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	if (!open || loading) return <BookingModalSkeleton />;

	const selectedWhen = selTime ? `${dayjs(date).format("ddd D MMM")} · ${formatTime24To12(selTime, language)}` : "";

	/* A home booking goes to the address the customer typed; everything else
	   happens at the shop. Skipped entirely when neither is on file. */
	const whereLine = hasHomeService ? homeAddress.address : shopData.address;

	const staffLabel = selectedStaffData ? `${selectedStaffData.firstName || ""} ${selectedStaffData.lastName || ""}`.trim() : t.staff.any;

	/* The review reads back what the shop will actually receive, so it takes
	   the account's details when there are any and the typed ones otherwise —
	   the same precedence the payload uses. */
	const reviewName = isLoggedIn ? `${userData?.user?.firstName || ""} ${userData?.user?.lastName || ""}`.trim() : customerName;
	const reviewEmail = isLoggedIn ? userData?.user?.email : customerEmail;
	const contactLines = [reviewName, reviewEmail, contactPhone].filter(Boolean);

	const totalLine = [totalTime ? `${totalTime} min` : "", totalPrice ? formatPrice(totalPrice, currency) : ""].filter(Boolean).join(" · ");

	/* Same row on the services step and again on the review — the second one
	   just can't be removed from there. */
	function renderServiceRow(s, removable) {
		const loc = s.locationType || (s.isHomeService ? "home" : "in_shop");

		return (
			<div key={s.serviceId} className={`sf-bk-service${removable ? "" : " is-static"}`}>
				<div className="sf-bk-service-copy">
					<span className="sf-bk-service-name">
						<span>{s.name}</span>
						{loc === "home" && <em className="sf-bk-tag is-home">{t.serviceType.home}</em>}
						{loc === "online" && <em className="sf-bk-tag is-online">{t.serviceType.online}</em>}
					</span>
					<span className="sf-bk-service-meta">
						<strong>{formatPrice(s.price, s.currency)}</strong>
						<em aria-hidden="true">·</em>
						{s.duration} min
					</span>
				</div>
				{removable && (
					<button
						type="button"
						className="sf-bk-service-remove"
						aria-label={t.services.remove}
						onClick={() => setSelectedServices((p) => p.filter((srv) => srv.serviceId !== s.serviceId))}
					>
						<X size={15} strokeWidth={2.4} />
					</button>
				)}
			</div>
		);
	}

	return (
		<div
			className="sf-bk-overlay"
			onTouchMove={(e) => {
				if (e.target === e.currentTarget) e.preventDefault();
			}}
		>
			<div
				className="sf-bk-modal"
				role="dialog"
				aria-modal="true"
				aria-label={shopData.name}
			>
				{/* HEADER */}
				<div className="sf-bk-header">
					<button type="button" className="sf-bk-back" onClick={onClose} aria-label={t.buttons.close}>
						<ArrowLeft size={19} strokeWidth={2.2} />
					</button>
					<h2 className="sf-bk-title">{shopData.name}</h2>
				</div>

				<div className="sf-bk-body" ref={bodyRef}>
					{/* There's no step strip above, so this heading is the only
					    thing naming which of the two steps you're on. */}
					{/* <header className="sf-bk-stephead">
						<h3 className="sf-bk-steptitle">{stepId === "review" ? t.review.title : steps[stepIndex].label}</h3>
						<p className="sf-bk-stephint">{t.stepHints[stepId]}</p>
					</header> */}

					{/* ---------- STEP 1: SERVICES + WHEN + CONTACT ---------- */}
					{stepId === "book" && (
						<>
							{/* SERVICES */}
							<section className="sf-bk-section">
								<h3 className="sf-bk-label">{t.services.title}</h3>

								{selectedServices.length > 0 && <div className="sf-bk-services">{selectedServices.map((s) => renderServiceRow(s, true))}</div>}

								{filteredServices.length > selectedServices.length && (
									<select
										className="sf-bk-select"
										onChange={(e) => {
											const serviceId = e.target.value;
											if (!serviceId) return;

											// Only allow services this staff can actually perform
											const s = filteredServices.find((srv) => String(srv.serviceId) === String(serviceId));

											if (!canAddService(s)) {
												setToast(t.services.cantMix);
												e.target.value = "";
												return;
											}

											if (!s) {
												// Safety fallback (should never happen)
												console.warn("Service not allowed for this staff");
												e.target.value = "";
												return;
											}

											setSelectedServices((p) => [...p, s]);
											e.target.value = "";
										}}
										defaultValue=""
									>
										<option value="" disabled>
											{t.services.add}
										</option>

										{filteredServices
											.filter((s) => {
												if (s.isHomeService === true) return true;

												return !selectedServices.some((sel) => sel.serviceId === s.serviceId);
											})
											.map((s) => {
												const disabled = !canAddService(s);
												const loc = s.locationType || (s.isHomeService ? "home" : "in_shop");
												const kind = loc === "home" ? ` • ${t.serviceType.home}` : loc === "online" ? ` • ${t.serviceType.online}` : "";

												return (
													<option key={s.serviceId} value={s.serviceId} disabled={disabled}>
														{`${s.name}${kind} • ${s.duration} min • ${formatPrice(s.price, s.currency)}${disabled ? t.services.notMixable : ""}`}
													</option>
												);
											})}
									</select>
								)}

								{/* Was nested inside the <select> above, where no browser paints it.
								    Only worth saying when something in the list is actually blocked,
								    otherwise it sits there from the first service onward. */}
								{lockedType && filteredServices.some((s) => !canAddService(s)) && (
									<p className="sf-bk-helper">
										<Info size={14} strokeWidth={2.2} />
										{lockedType === "home" ? t.services.lockedHome : t.services.lockedShop}
									</p>
								)}
							</section>

							{/* STAFF */}
							<StaffDropdown
								staffList={dayAvailability
									.filter((s) => getValidSlotsForStaff(s).length > 0)
									.map((s) => ({ ...s, slotsCount: getValidSlotsForStaff(s).length }))}
								selectedStaffId={selectedstaff}
								onSelect={(id) => {
									setSelectedstaff(id);
									setStaffDropdownOpen(false);
								}}
								language={language}
								label={t.staff.title}
								open={staffDropdownOpen}
								onToggle={() => setStaffDropdownOpen((p) => !p)}
							/>

							{/* WHEN */}
							<section className="sf-bk-section-more-space">
								<h3 className="sf-bk-label">{t.steps.when}</h3>

								<div className="sf-bk-weekhead">
									<button type="button" className="sf-bk-navbtn" onClick={() => changeWeek(-1)} aria-label={t.week.previous}>
										<ChevronLeft size={18} strokeWidth={2.2} />
									</button>
									<span className="sf-bk-weeklabel">
										{dayjs(startDate).format("MMM D")} – {dayjs(startDate).add(6, "day").format("MMM D")}
									</span>
									<button type="button" className="sf-bk-navbtn" onClick={() => changeWeek(1)} aria-label={t.week.next}>
										<ChevronRight size={18} strokeWidth={2.2} />
									</button>
								</div>

								<div className="sf-bk-days" ref={daysRef}>
									{Object.entries(timeData.week).map(([dateStr, d], i) => {
										const isSel = dayIndex === i;

										// Determine bar status
										let barStatus = "open"; // default
										if (d.status === "closed") barStatus = "closed";
										else if (d.status === "full" || d.totalSlots === 0) barStatus = "full";
										else if (d.totalSlots < 10) barStatus = "busy";
										const localeMap = { en: "en-US", es: "es-ES" };

										return (
											<button
												key={dateStr}
												type="button"
												className={`sf-bk-day ${isSel ? "is-active" : ""}`}
												aria-pressed={isSel}
												onClick={() => {
													if (d.status !== "closed" && d.status !== "full") {
														setDayIndex(i);
														setDate(dateStr);
														setSelTime("");
													}
												}}
												disabled={d.status === "closed" || d.status === "full"}
											>
												<span className="sf-bk-day-name">
													{parseLocalDate(dateStr).toLocaleDateString(localeMap[language], { weekday: "short" })}
												</span>
												<span className="sf-bk-day-num">{parseLocalDate(dateStr).getDate()}</span>
												<span className={`sf-bk-day-bar ${barStatus}`} aria-hidden="true" />
											</button>
										);
									})}
								</div>

								{/* PERIOD FILTERS */}
								<div className="sf-bk-periods">
									{["Morning", "Afternoon", "Evening"].map((label) => {
										const isAvailable = availablePeriods.has(label);

										return (
											<button
												key={label}
												type="button"
												className={`sf-bk-period ${timePeriod === label ? "is-active" : ""}`}
												aria-pressed={timePeriod === label}
												onClick={() => {
													if (!isAvailable) return; // prevent clicking unavailable filters
													setTimePeriod(label);
													if (!slotsRef.current) return;

													const ranges = { Morning: 0, Afternoon: 12, Evening: 17 };

													const targetBtn = Array.from(slotsRef.current.querySelectorAll(".sf-bk-slot")).find((btn, index) => {
														const slot = slotsToShow[index];
														const hour = parseInt(slot.start.split(":")[0], 10);
														return hour >= ranges[label];
													});

													if (targetBtn) {
														targetBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
													}
												}}
												disabled={!isAvailable}
											>
												{t.timeFilters[label.toLowerCase()]}
											</button>
										);
									})}
								</div>

								{/* TIME SLOTS */}
								<div className="sf-bk-slots-row">
									{slotsToShow.length > 0 && (
										<button
											type="button"
											className={`sf-bk-scrollbtn ${!canScrollLeft ? "is-disabled" : ""}`}
											onClick={() => {
												if (slotsRef.current) slotsRef.current.scrollBy({ left: -300, behavior: "smooth" });
											}}
											aria-hidden="true"
											tabIndex={-1}
										>
											<ChevronLeft size={18} strokeWidth={2.2} />
										</button>
									)}

									<div
										className="sf-bk-slots"
										ref={slotsRef}
										onScroll={() => {
											if (!slotsRef.current) return;
											const { scrollLeft, scrollWidth, clientWidth } = slotsRef.current;
											setCanScrollLeft(scrollLeft > 0);
											setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
										}}
									>
										{slotsToShow.length > 0 ? (
											slotsToShow.map((slot) => (
												<button
													key={slot.start}
													type="button"
													className={`sf-bk-slot ${selTime === slot.start ? "is-selected" : ""}`}
													aria-pressed={selTime === slot.start}
													onClick={() => setSelTime(slot.start)}
												>
													{formatTime24To12(slot.start, language)}
												</button>
											))
										) : (
											<div className="sf-bk-noslots">{t.noSlots}</div>
										)}
									</div>

									{slotsToShow.length > 0 && (
										<button
											type="button"
											className={`sf-bk-scrollbtn ${!canScrollRight ? "is-disabled" : ""}`}
											onClick={() => {
												if (slotsRef.current) slotsRef.current.scrollBy({ left: 300, behavior: "smooth" });
											}}
											aria-hidden="true"
											tabIndex={-1}
										>
											<ChevronRight size={18} strokeWidth={2.2} />
										</button>
									)}
								</div>
							</section>

							{/* GUEST INFO */}
							{needsGuestInfo && (
								<section className="sf-bk-section">
									<h3 className="sf-bk-label">{t.guest.title}</h3>
									<input
										className="sf-bk-input"
										type="text"
										placeholder={t.guest.fullName}
										value={customerName}
										onChange={(e) => setCustomerName(e.target.value)}
									/>
									<input
										className="sf-bk-input"
										type="email"
										placeholder={t.guest.email}
										value={customerEmail}
										onChange={(e) => setCustomerEmail(e.target.value)}
									/>
									<PhoneInput businessPhone={homePhone} setBusinessPhone={setHomePhone} />
								</section>
							)}

							{/* CONTACT NUMBER — logged in, but nothing on the account to send.
							    Without this the booking just failed on submit with no way to
							    fix it from here. */}
							{needsContactPhone && (
								<section className="sf-bk-section">
									<h3 className="sf-bk-label">{t.contact.title}</h3>
									<p className="sf-bk-helper">
										<Info size={14} strokeWidth={2.2} />
										{t.contact.hint}
									</p>
									<PhoneInput businessPhone={homePhone} setBusinessPhone={setHomePhone} />
								</section>
							)}

							{/* HOME SERVICE DETAILS */}
							{needsHomeDetails && (
								<section className="sf-bk-section">
									<h3 className="sf-bk-label">{t.home.title}</h3>

									<RSFNewAutocomplete
										value={homeAddress.address}
										onChange={(val) => {
											setHomeAddress((prev) => ({ ...prev, address: val, lat: null, lng: null }));
										}}
										onSelect={(data) => {
											setHomeAddress({
												address: data.address,
												city: data.city,
												country: data.country,
												lat: data.lat,
												lng: data.lng,
											});
										}}
										language={language}
									/>

									<input
										className="sf-bk-input"
										type="text"
										placeholder={t.home.extraDetails}
										value={addressDescription}
										onChange={(e) => setAddressDescription(e.target.value)}
									/>
								</section>
							)}
						</>
					)}

					{/* ---------- STEP 2: REVIEW ---------- */}
					{stepId === "review" && (
						<section className="sf-bk-section">
							<div className="sf-bk-review">
								<div className="sf-bk-review-row">
									<span className="sf-bk-review-k">
										<CalendarDays size={14} strokeWidth={2.2} />
										{t.review.when}
									</span>
									<span className="sf-bk-review-v">{selectedWhen}</span>
								</div>

								<div className="sf-bk-review-row">
									<span className="sf-bk-review-k">
										<MapPin size={14} strokeWidth={2.2} />
										{t.review.where}
									</span>
									<span className="sf-bk-review-v">
										{hasHomeService ? t.review.homeVisit : t.review.atShop}
										{whereLine && <small>{whereLine}</small>}
										{hasHomeService && addressDescription && <small>{addressDescription}</small>}
									</span>
								</div>

								<div className="sf-bk-review-row">
									<span className="sf-bk-review-k">
										<User size={14} strokeWidth={2.2} />
										{t.review.professional}
									</span>
									<span className="sf-bk-review-v">{staffLabel}</span>
								</div>

								{contactLines.length > 0 && (
									<div className="sf-bk-review-row">
										<span className="sf-bk-review-k">
											<Phone size={14} strokeWidth={2.2} />
											{t.review.contact}
										</span>
										<span className="sf-bk-review-v">
											{contactLines[0]}
											{contactLines.slice(1).map((line) => (
												<small key={line}>{line}</small>
											))}
										</span>
									</div>
								)}
							</div>

							<h3 className="sf-bk-label">{t.review.services}</h3>
							<div className="sf-bk-services">{selectedServices.map((s) => renderServiceRow(s, false))}</div>

							<div className="sf-bk-review-total">
								<span>{t.review.total}</span>
								<strong>{totalLine}</strong>
							</div>
						</section>
					)}
				</div>

				{validationError && <BannerMessageFixed type="error" text={validationError} />}
				{toast && <div className="sf-bk-toast">{toast}</div>}

				{/* FOOTER */}
				<div className="sf-bk-footer">
					{/* The running tally belongs on the step you're still choosing on —
					    the review lays the same numbers out in full. */}
					{stepId !== "review" && (
						<div className="sf-bk-summary">
							<span className="sf-bk-summary-when">
								<Clock size={13} strokeWidth={2.2} />
								<span>{selectedWhen || totalServicesText(selectedServices.length)}</span>
							</span>
							<span className="sf-bk-summary-total">{totalLine}</span>
						</div>
					)}

					<div className="sf-bk-actions">
						{stepIndex > 0 && (
							<button type="button" className="sf-bk-secondary" onClick={() => goToStep(stepIndex - 1)} disabled={confirmLoading}>
								{t.buttons.back}
							</button>
						)}
						<button
							type="button"
							className="sf-bk-confirm"
							onClick={isLastStep ? confirmBooking : goNext}
							disabled={confirmLoading}
						>
							{confirmLoading ? (
								<span className="sf-bk-spinner" />
							) : !isLastStep ? (
								t.buttons.next
							) : mode === "reschedule" ? (
								t.buttons.confirmReschedule
							) : (
								t.buttons.confirm
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BookingModal;
